import { useState, useEffect, useCallback, useRef } from 'react';
import { DefaultService, ApiError } from '../generated-api';

type JobType = 'rules_processing' | 'strategy_generation' | 'rating_recalculation';

interface JobPollerState {
  isProcessing: boolean;
  statusText: string;
  error: string | null;
}

interface JobPollerReturn extends JobPollerState {
  startJob: (jobId: string) => void;
  checkForJob: () => void;
}

const storageKey = (gameId: string, jobType: JobType) => `job_${gameId}_${jobType}`;

const STATUS_LABELS: Record<JobType, Record<string, string>> = {
  rules_processing: {
    pending: 'Queued for processing...',
    processing: 'Processing rules...',
  },
  strategy_generation: {
    pending: 'Queued for strategy generation...',
    processing: 'Generating strategy guide...',
  },
  rating_recalculation: {
    pending: 'Queued for rating recalculation...',
    processing: 'Recalculating all ratings...',
  },
};

export function useJobPoller(
  gameId: string,
  jobType: JobType,
  onComplete?: () => void,
): JobPollerReturn {
  const [state, setState] = useState<JobPollerState>({
    isProcessing: false,
    statusText: '',
    error: null,
  });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const clearJob = useCallback(() => {
    localStorage.removeItem(storageKey(gameId, jobType));
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setState({ isProcessing: false, statusText: '', error: null });
  }, [gameId, jobType]);

  const poll = useCallback(async (jobId: string) => {
    try {
      const job = await DefaultService.getJobStatusJobsJobIdGet({ jobId });
      if (job.status === 'completed') {
        clearJob();
        onCompleteRef.current?.();
      } else if (job.status === 'failed') {
        const errorMsg = job.error || 'Processing failed';
        localStorage.removeItem(storageKey(gameId, jobType));
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setState({ isProcessing: false, statusText: '', error: errorMsg });
      } else {
        setState({
          isProcessing: true,
          statusText: STATUS_LABELS[jobType][job.status] || 'Processing...',
          error: null,
        });
      }
    } catch (err) {
      // If the job no longer exists (expired by TTL or deleted), clear it
      if (err instanceof ApiError && err.status === 404) {
        clearJob();
        return;
      }
      console.error('Job poll error:', err);
      // Don't clear on transient network errors - keep polling
    }
  }, [gameId, jobType, clearJob]);

  const startPolling = useCallback((jobId: string) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setState({
      isProcessing: true,
      statusText: STATUS_LABELS[jobType].pending,
      error: null,
    });
    poll(jobId);
    intervalRef.current = setInterval(() => poll(jobId), 5000);
  }, [jobType, poll]);

  const startJob = useCallback((jobId: string) => {
    localStorage.setItem(storageKey(gameId, jobType), jobId);
    startPolling(jobId);
  }, [gameId, jobType, startPolling]);

  const checkForJob = useCallback(() => {
    if (intervalRef.current) return; // already polling
    const existingJobId = localStorage.getItem(storageKey(gameId, jobType));
    if (existingJobId) {
      startPolling(existingJobId);
    }
  }, [gameId, jobType, startPolling]);

  // On mount, resume polling if there's an active job
  useEffect(() => {
    checkForJob();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkForJob]);

  return { ...state, startJob, checkForJob };
}
